import { useMemo } from 'react'
import { Box, Divider, Stack, Tooltip, Typography, styled, tooltipClasses } from '@mui/material'
import type { ReactNode } from 'react'
import type { BoxProps, TooltipProps } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

export interface IRichTooltipPanelConfig {
  title: ReactNode
  content: ReactNode
}

export interface IRichTooltipProps {
  panel?: IRichTooltipPanelConfig
  renderPanel?: () => ReactNode
  children: React.ReactNode
  /** Optional: manually set touch mode. If not provided, auto-detect touch device */
  isTouch?: boolean
  slots?: {
    tooltipProps?: Partial<TooltipProps>
    wrapperProps?: BoxProps
  }
}

/**
 * Detect if the current device supports touch
 */
const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    (navigator.msMaxTouchPoints || 0) > 0 ||
    window.matchMedia?.('(pointer: coarse)').matches
  )
}

export const RichTooltip = (props: IRichTooltipProps) => {
  const { children, panel, renderPanel, slots, isTouch } = props

  // Auto-detect touch device if isTouch prop is not provided
  const shouldUseTouchMode = useMemo(() => {
    return isTouch !== undefined ? isTouch : isTouchDevice()
  }, [isTouch])

  const renderTitle = (value: ReactNode) => {
    if (!value) return null
    if (typeof value !== 'string') return value
    return (
      <Typography variant='subtitle1' className={richTooltipClasses.title}>
        {value}
      </Typography>
    )
  }

  const renderContent = (value: ReactNode) => {
    if (!value) return null
    if (typeof value !== 'string') return value
    return <Typography variant='body2'>{value}</Typography>
  }

  const renderTitlePanel = () => {
    if (renderPanel) return renderPanel()
    if (!panel) return <Typography variant='body2'>Please provide a panel configuration</Typography>
    return (
      <TooltipTitlePanelStyled className={richTooltipClasses.titlePanel}>
        {renderTitle(panel.title)}
        <Divider />
        <Box className={richTooltipClasses.content}>{renderContent(panel.content)}</Box>
      </TooltipTitlePanelStyled>
    )
  }

  // Merge touch-specific props with user-provided tooltip props
  const tooltipProps: Partial<TooltipProps> = {
    ...(shouldUseTouchMode ? { enterTouchDelay: 0, leaveTouchDelay: 3000 } : {}),
    ...slots?.tooltipProps
  }

  return (
    <TooltipStyled arrow placement='bottom' title={renderTitlePanel()} className={richTooltipClasses.root} {...tooltipProps}>
      <Box {...slots?.wrapperProps}>{children}</Box>
    </TooltipStyled>
  )
}

export default RichTooltip

const richTooltipClasses = {
  root: 'RichTooltip-root',
  titlePanel: 'RichTooltip-titlePanel',
  title: 'RichTooltip-title',
  content: 'RichTooltip-content'
}

const getRichTooltipClass = (key: keyof typeof richTooltipClasses, options?: { prefix?: string; suffix?: string }) => {
  return `${options?.prefix || ''}.${richTooltipClasses[key]}${options?.suffix || ''}`
}

const TooltipTitlePanelStyled = styled(Stack)(({ theme }) => ({
  [getRichTooltipClass('titlePanel', { prefix: '&' })]: {
    gap: '6px'
  },
  [getRichTooltipClass('title')]: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    padding: theme.spacing(1, 1.5, 0)
  },
  [getRichTooltipClass('content')]: {
    padding: theme.spacing(1, 1.5, 1.5),
    color: theme.palette.text.primary
  }
}))

const TooltipStyled = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: 0,
    minWidth: '360px',
    maxWidth: '450px',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4) 0px 6px 30px' : 'rgba(0, 0, 0, 0.08) 0px 6px 30px',
    border: `1px solid ${theme.palette.divider}`
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper
  }
}))

export const HelpOutlinePulseIcon = styled(HelpOutlineIcon)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  color: theme.palette.primary.main,
  borderRadius: '50%',
  cursor: 'help',
  animation: 'pulseGlow 1.5s infinite',
  '@keyframes pulseGlow': {
    '0%': {
      boxShadow: theme.palette.mode === 'dark' ? '0 0 0 0 rgba(144, 202, 249, 0.3)' : '0 0 0 0 rgba(33, 150, 243, 0.3)'
    },
    '70%': {
      boxShadow: theme.palette.mode === 'dark' ? '0 0 0 10px rgba(144, 202, 249, 0)' : '0 0 0 10px rgba(33, 150, 243, 0)'
    },
    '100%': {
      boxShadow: theme.palette.mode === 'dark' ? '0 0 0 0 rgba(144, 202, 249, 0)' : '0 0 0 0 rgba(33, 150, 243, 0)'
    }
  }
}))
